/**
 * English Translations
 *
 * This file contains all English translations for the application.
 * It extends React Admin's default English translations.
 */

import englishMessages from 'ra-language-english';

// Feature translations
import { authEn } from '@/features/auth/i18n';
import { accountsEn } from '@/features/accounts/i18n';
import { testEn } from '@/features/test/i18n';
import { creditConsumptionEn } from '@/features/credit-consumption/i18n';
import { costCenterEn } from '@/features/cost-center/i18n';
import { pricingEn } from '@/features/pricing/i18n';
import { creditBalanceEn } from '@/features/credit-balance/i18n';
import { creditTransactionEn } from '@/features/credit-transaction/i18n';
import { creditEventLogsEn } from '@/features/credit-event-logs/i18n';
import { creditEventSummaryEn } from '@/features/credit-event-summary/i18n';
import { userManagementEn } from '@/features/user-management/i18n';
import { platformUsersEn } from '@/features/platform-users/i18n';
import { scheduledJobsEn } from '@/features/scheduled-jobs/i18n';
import { statisticsEn } from '@/features/statistics/i18n';
import { notificationLogsEn } from '@/features/notification-logs/i18n';
import { applicationsEn } from '@/features/applications/i18n';
import { licensesEn } from '@/features/licenses/i18n';
import { providersEn } from '@/features/providers/i18n';
import { defaultPricingEn } from '@/features/default-pricing/i18n';
import { manageGroupsEn } from '@/features/manage-groups/i18n';
import { backupEn } from '@/features/backup/i18n';
import { userProfileEn } from '@/features/user-profile/i18n';

const customEnglishMessages = {
  ...englishMessages,

  common: {
    no_data_title: 'No data found',
    no_data_description: 'There are no records to display.',
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Operation completed successfully',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    clear: 'Clear',
    yes: 'Yes',
    no: 'No',
    coming_soon: 'Coming soon',
    phone: {
      prefix: 'Prefix',
      number: 'Number',
    },
    deleteConfirmTitle: 'Are you sure to delete?',
    deleteConfirmSubtitle: 'This cannot be undone',
    ok: 'OK',
    status: {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
    },
  },

  menu: {
    overview: 'Overview',
    groups: {
      administration: 'Administration',
    },
  },

  auth: {
    session: 'Session',
    session_expired: 'Session expired',
  },

  pages: {
    login: {
      ...authEn,
      title: 'Welcome',
      subtitle: 'Sign in to your account',
      login_button: 'Sign In',
      forgot_password: 'Forgot password?',
    },
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome back',
    },
    settings: {
      title: 'Settings',
      configuration: 'Configuration',
      configurationIntro: 'Pick a configuration section from the menu.',
      defaultParameters: 'Default parameters',
      defaultParametersIntro: 'Platform-wide default parameters.',
    },
  },

  resources: {
    accounts: accountsEn,
    // RA's resource name is `apps/accounts`; alias the i18n bundle so
    // lookups like `resources.apps/accounts.notifications.deleted`
    // resolve to the same translations as `resources.accounts.*`.
    'apps/accounts': accountsEn,
    test: testEn,
    'credit-consumption': creditConsumptionEn,
    'credit-consumption/cost-center': costCenterEn,
    'credit-consumption/pricing': pricingEn,
    'credit-consumption/credit-balance': creditBalanceEn,
    'credit-consumption/credit-transaction': creditTransactionEn,
    'credit-consumption/credit-event-logs': creditEventLogsEn,
    'credit-consumption/credit-event-summary': creditEventSummaryEn,
    'user-management': userManagementEn,
    'platform-users': platformUsersEn,
    'scheduled-jobs': scheduledJobsEn,
    statistics: statisticsEn,
    'notification-logs': notificationLogsEn,
    applications: applicationsEn,
    licenses: licensesEn,
    providers: providersEn,
    'default-pricing': defaultPricingEn,
    'manage-groups': manageGroupsEn,
    backup: backupEn,
  },

  // Top-level namespace for non-Resource features (custom routes).
  'user-profile': userProfileEn,

  validation: {
    required: 'This field is required',
    email: 'Please enter a valid email address',
    minLength: 'Must be at least %{min} characters',
    maxLength: 'Must be no more than %{max} characters',
    number: 'Must be a valid number',
    positive: 'Must be a positive number',
    nonNegative: 'Must be 0 or greater',
    unique: 'This value already exists',
  },

  notifications: {
    session_expired: 'Your session has expired. Please log in again.',
    connection_error: 'Connection error. Please check your internet connection.',
    server_error: 'Server error. Please try again later.',
  },
};

export default customEnglishMessages;
